import { useMemo, useRef, useCallback, useState } from 'react';

interface RackEquipmentItem {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  weight_kg: number;
  attributes: any;
}

interface RackCanvasProps {
  uSlots: number;
  occupiedPositions: number[];
  equipmentList: RackEquipmentItem[];
  maxWeightKg: number;
  currentWeight: number;
  warnings?: string[];
  errors?: string[];
  onSlotClick?: (position: number) => void;
}

const TYPE_COLORS: Record<string, string> = {
  server: '#3b82f6',
  switch: '#8b5cf6',
  storage: '#f59e0b',
  firewall: '#ef4444',
  router: '#ec4899',
  pdu: '#10b981',
  ups: '#6366f1',
  patch_panel: '#14b8a6',
};

const TYPE_ICONS: Record<string, string> = {
  server: '🖥️',
  switch: '🔀',
  storage: '💾',
  firewall: '🛡️',
  router: '🌐',
  pdu: '⚡',
  ups: '🔋',
  patch_panel: '🔌',
  unknown: '📦',
};

export function RackCanvas({
  uSlots,
  occupiedPositions,
  equipmentList,
  maxWeightKg,
  currentWeight,
  warnings = [],
  errors = [],
  onSlotClick,
}: RackCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);

  const slotHeight = useMemo(() => {
    const maxH = 400;
    const minH = 20;
    const ideal = Math.max(minH, Math.min(maxH, maxH / uSlots));
    return ideal;
  }, [uSlots]);

  const slots = useMemo(() => {
    return Array.from({ length: uSlots }, (_, i) => uSlots - i);
  }, [uSlots]);

  // Group equipment by starting position for rendering
  const equipmentAtPosition = useMemo(() => {
    const map = new Map<number, RackEquipmentItem>();
    equipmentList.forEach(eq => {
      if (!map.has(eq.u_position)) {
        map.set(eq.u_position, eq);
      }
    });
    return map;
  }, [equipmentList]);

  const weightPct = maxWeightKg ? (currentWeight / maxWeightKg) * 100 : 0;

  const handleSlotClick = useCallback(() => {
    if (hoveredSlot !== null && onSlotClick) {
      onSlotClick(hoveredSlot);
    }
  }, [hoveredSlot, onSlotClick]);

  return (
    <div className="flex gap-4">
      {/* Rack visualization */}
      <div ref={containerRef} className="flex-1 overflow-auto">
        <div
          className="relative rounded-lg bg-bg-panel border border-gray-700"
          style={{ minWidth: 280 }}
          onMouseLeave={() => setHoveredSlot(null)}
        >
          {/* Rack ears (mounting rails) */}
          <div className="absolute left-3 right-3 top-3 bottom-3 flex flex-col-reverse">
            {slots.map(u => {
              const eq = equipmentAtPosition.get(u);
              const isOccupied = occupiedPositions.includes(u);
              const isHovered = hoveredSlot === u;

              return (
                <div
                  key={u}
                  className={`
                    relative border-b border-gray-600 flex items-center
                    ${isOccupied ? 'cursor-default' : onSlotClick ? 'cursor-pointer hover:bg-blue-500/10' : ''}
                  `}
                  style={{ height: `${slotHeight}px` }}
                  onMouseEnter={() => setHoveredSlot(u)}
                  onClick={handleSlotClick}
                >
                  {/* Mounting holes (left rail) */}
                  <div className="absolute left-1 flex flex-col gap-1" style={{ top: '15%', bottom: '15%' }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                  </div>

                  {/* U number */}
                  <span className="text-text-muted w-7 text-right mr-1 text-xs font-mono select-none">
                    {u}U
                  </span>

                  {/* Equipment block */}
                  {eq && (
                    <div
                      className="flex-1 mx-1 px-2 rounded flex items-center gap-2 text-sm font-medium text-white truncate shadow-md"
                      style={{
                        backgroundColor: TYPE_COLORS[eq.equipment_type] || '#6b7280',
                        minHeight: `${slotHeight - 6}px`,
                      }}
                      title={`${eq.name} (${eq.u_height}U)`}
                    >
                      <span className="text-base">{TYPE_ICONS[eq.equipment_type] || '📦'}</span>
                      <span className="truncate">{eq.name}</span>
                      <span className="text-xs opacity-70 shrink-0">{eq.u_height}U</span>
                    </div>
                  )}

                  {/* Hover indicator for free slot */}
                  {!isOccupied && isHovered && onSlotClick && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-6 h-6 rounded-full border-2 border-dashed border-blue-400/50 flex items-center justify-center">
                        <span className="text-blue-400 text-lg leading-none">+</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info sidebar */}
      <div className="w-56 space-y-3 shrink-0">
        {/* Capacity */}
        <div className="p-3 rounded-lg bg-bg-panel border border-border">
          <div className="text-xs text-text-muted uppercase tracking-wide mb-2">Вместимость</div>
          <div className="flex justify-between text-sm mb-1">
            <span>Установлено:</span>
            <span className="font-mono">{equipmentList.length}U</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span>Свободно:</span>
            <span className="font-mono text-green-400">{uSlots - occupiedPositions.length}U</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Всего слотов:</span>
            <span className="font-mono">{uSlots}U</span>
          </div>
        </div>

        {/* Weight */}
        <div className="p-3 rounded-lg bg-bg-panel border border-border">
          <div className="text-xs text-text-muted uppercase tracking-wide mb-2">Вес</div>
          <div className="text-sm font-mono mb-2">
            {currentWeight.toFixed(1)} / {maxWeightKg} кг
          </div>
          <div className="h-2.5 rounded-full bg-bg-input overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                weightPct > 100
                  ? 'bg-status-critical animate-pulse'
                  : weightPct > 80
                  ? 'bg-status-warning'
                  : 'bg-accent'
              }`}
              style={{ width: `${Math.min(weightPct, 100)}%` }}
            />
          </div>
          <div className="text-xs text-text-muted mt-1 text-right">
            {weightPct.toFixed(0)}%
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="text-xs font-semibold text-status-critical mb-2">⛇ Ошибки</div>
            <div className="text-xs text-status-critical space-y-1">
              {errors.map((e, i) => (
                <div key={i}>• {e}</div>
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
            <div className="text-xs font-semibold text-status-warning mb-2">⚠️ Предупреждения</div>
            <div className="text-xs text-status-warning space-y-1">
              {warnings.map((w, i) => (
                <div key={i}>• {w}</div>
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="p-3 rounded-lg bg-bg-panel border border-border">
          <div className="text-xs text-text-muted uppercase tracking-wide mb-2">Типы</div>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(TYPE_ICONS).slice(0, 8).map(([type, icon]) => (
              <div key={type} className="text-xs flex items-center gap-1">
                <span>{icon}</span>
                <span className="capitalize text-text-secondary">{type.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
